/**
 * AgentList Component
 * Lista de agentes con grid responsive
 */

import { Box, Typography, CircularProgress, Grid } from '@mui/material';
import type { GridProps } from '@mui/material';
import { AgentCard } from './AgentCard';
import { useAgentsStore } from '@/store/agentsStore';
import type { Agent } from '@/types/agent';

interface AgentListProps {
  agents: Agent[];
  loading?: boolean;
  onAgentClick?: (agentId: string) => void;
  onConnect?: (agentId: string) => void;
  onConsole?: (agentId: string) => void;
  onDetails?: (agentId: string) => void;
  viewMode?: 'grid' | 'list' | 'listao';
}

export const AgentList: React.FC<AgentListProps> = ({
  agents,
  loading = false,
  onAgentClick,
  onConnect,
  onConsole,
  onDetails,
  viewMode = 'grid',
}) => {
  const { selectedAgentId, filters } = useAgentsStore();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (agents.length === 0) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {filters.search
            ? 'Sin resultados'
            : !filters.showOffline
              ? 'No hay agentes conectados'
              : 'Sin agentes aprobados'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {filters.search
            ? 'Probá con otro término de búsqueda.'
            : !filters.showOffline
              ? 'Activá "Mostrar offline" para verlos.'
              : 'Aprobá un enrollment para comenzar.'}
        </Typography>
      </Box>
    );
  }

  // Adjust grid sizing based on viewMode: list/listao => full-width rows, grid => responsive tiles
  const itemSize: Partial<GridProps> =
    viewMode === 'list' || viewMode === 'listao'
      ? { size: { xs: 12 } }
      : { size: { xs: 12, sm: 6, md: 4, lg: 3 } };

  return (
    <Grid container spacing={2}>
      {agents.map((agent) => (
        <Grid {...itemSize} key={agent.agentId}>
          <AgentCard
            agent={agent}
            selected={agent.agentId === selectedAgentId}
            onClick={() => onAgentClick?.(agent.agentId)}
            onConnect={() => onConnect?.(agent.agentId)}
            onConsole={() => onConsole?.(agent.agentId)}
            onDetails={() => onDetails?.(agent.agentId)}
            viewMode={viewMode}
          />
        </Grid>
      ))}
    </Grid>
  );
};
